import { useState, useEffect } from 'react';
import { Sidebar } from '../moderator/Sidebar';
import config from '../../config/config';
import { Navbar } from '../Navbar';
import { Link } from 'react-router-dom';

export default function ViewService() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const selectedYear = localStorage.getItem('qurbani_year') || new Date().getFullYear();
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseUrl}/api/services/getService?year=${selectedYear}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch services');
      const data = await response.json();
      setServices(data);
    } catch (error) {
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseUrl}/api/services/service/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete');
      setServices(services.filter(service => service.id !== id));
      alert('Service deleted successfully');
    } catch (error) {
      alert('Failed to delete service');
    }
  };

  // Logic for Filtering and Pagination
  const filteredServices = services.filter(service => {
    const searchLower = searchTerm.toLowerCase();
    return Object.values(service).some(val => String(val).toLowerCase().includes(searchLower));
  });

  const totalPages = Math.ceil(filteredServices.length / entriesPerPage);
  const indexOfLastItem = currentPage * entriesPerPage;
  const indexOfFirstItem = indexOfLastItem - entriesPerPage;
  const displayedServices = filteredServices.slice(indexOfFirstItem, indexOfLastItem);
  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar /><Sidebar />
      <div className="ml-64 pt-24 p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div></div>
    </div>
  );

  return (
      <main className="">
        {/* Theme Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Other Services</h1>
            </div>
            <Link to="/othService/add" className="bg-indigo-800 text-white px-4 py-2.5 rounded-lg font-semibold hover:bg-indigo-700 transition-all flex items-center gap-2 shadow-sm">
              <span className="text-xl">+</span> 
            </Link>
          </div>

          {/* Search & Filter Bar */}
          <div className="px-6 py-4 border-t border-gray-50 flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50/50">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-400 uppercase">Show</span>
              <select 
                className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-green-500"
                value={entriesPerPage}
                onChange={(e) => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="relative w-full md:w-80">
              <input
                type="text"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-500 transition-all"
              />
              <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
            </div>
          </div>
        </div>

        {/* Standardized Table Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-600 uppercase text-xs font-bold border-b border-gray-100">
                  <th className="px-6 py-4">ID</th>
                  <th className="px-6 py-4">Animal & Service</th>
                  <th className="px-6 py-4">Customer</th>
                  <th className="px-6 py-4 text-center">Day</th>
                  <th className="px-6 py-4">Payment</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {displayedServices.length === 0 ? (
                  <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-400">No services found matching your search.</td></tr>
                ) : (
                  displayedServices.map((service, index) => (
                    <tr key={service.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-gray-400 font-mono text-xs">#{indexOfFirstItem + index + 1}</td>
                      <td className="px-6 py-4">
                        <div className="font-bold text-gray-800">{service.service_type}</div>
                        <div className="text-[10px] text-green-600 font-bold uppercase">{service.type_of_animal}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-700">{service.first_name} {service.last_name}</div>
                        <div className="text-xs text-gray-400">{service.contact}</div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="px-2.5 py-1 bg-gray-100 rounded-md text-xs font-bold text-gray-600">{service.day}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-bold text-green-700 font-mono text-sm">Rs. {service.amount_paid}</div>
                        <div className="text-[10px] text-gray-400 uppercase">Due: Rs. {service.amount_due}</div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center gap-3">
                          <Link to={`/othservice/edit/${service.id}`} className="text-blue-600 hover:text-blue-800 font-medium">Edit</Link>
                          <button onClick={() => handleDelete(service.id)} className="text-red-600 hover:text-red-800 font-medium">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Standardized Pagination Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
            <span className="text-xs text-gray-500 font-medium">
              Showing {Math.min(displayedServices.length, (currentPage - 1) * entriesPerPage + 1)} to {Math.min(currentPage * entriesPerPage, filteredServices.length)} of {filteredServices.length}
            </span>
            
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border rounded-lg bg-white disabled:opacity-50 text-xs font-bold hover:bg-gray-100 transition-colors"
              >
                Prev
              </button>
              
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${currentPage === i + 1 ? 'bg-green-600 text-white shadow-md' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {i + 1}
                </button>
              ))}

              <button 
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border rounded-lg bg-white disabled:opacity-50 text-xs font-bold hover:bg-gray-100 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </main>
  );
}